import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../Utils/Axios";

export const registerUser = createAsyncThunk(
  "user/register",
  async (userData, thunkAPI) => {
    try {
      const response = await api.post("/api/user/register", userData);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data ||
        error.message ||
        "Network error";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const loginUser = createAsyncThunk(
  "user/login",
  async (credentials, thunkAPI) => {
    try {
      const response = await api.post("/api/auth/login", credentials);
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.message || error.message || "Login failed";
      const email = error.response?.data?.email || credentials?.email || "";
      const role = error.response?.data?.role || "";

      return thunkAPI.rejectWithValue({ status, message, email, role });
    }
  }
);

export const verifyEmailOtp = createAsyncThunk(
  "user/verifyEmailOtp",
  async ({ email, otp }, thunkAPI) => {
    try {
      const response = await api.post("/api/user/verify-email-otp", { email, otp });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "OTP verification failed"
      );
    }
  }
);

export const resendVerificationOtp = createAsyncThunk(
  "user/resendVerificationOtp",
  async (email, thunkAPI) => {
    try {
      const response = await api.post("/api/user/resend-verification-otp", { email });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Could not resend OTP"
      );
    }
  }
);

export const fetchUserDashboard = createAsyncThunk(
  "user/fetchDashboard",
  async (_, thunkAPI) => {
    try {
      const response = await api.get("/api/user/dashboard");
      return response.data.dashboard;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch dashboard"
      );
    }
  }
);

export const fetchUserProfile = createAsyncThunk(
  "user/fetchProfile",
  async (_, thunkAPI) => {
    try {
      const response = await api.get("/api/user/me");
      return response.data.user;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch profile"
      );
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  "user/updateProfile",
  async (payload, thunkAPI) => {
    try {
      const response = await api.put("/api/user/me", payload);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to update profile"
      );
    }
  }
);

export const uploadUserAvatar = createAsyncThunk(
  "user/uploadAvatar",
  async (file, thunkAPI) => {
    try {
      const formData = new FormData();
      formData.append("avatar", file);
      const response = await api.patch("/api/user/me/avatar", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to upload avatar"
      );
    }
  }
);

export const updateUserSettings = createAsyncThunk(
  "user/updateSettings",
  async (payload, thunkAPI) => {
    try {
      const response = await api.put("/api/user/settings", payload);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to update settings"
      );
    }
  }
);

export const changeUserPassword = createAsyncThunk(
  "user/changePassword",
  async (payload, thunkAPI) => {
    try {
      const response = await api.post("/api/user/change-password", payload);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to change password"
      );
    }
  }
);

export const fetchAppliedJobs = createAsyncThunk(
  "user/fetchAppliedJobs",
  async (_, thunkAPI) => {
    try {
      const response = await api.get("/api/user/applied-jobs");
      return response.data.jobs || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch applied jobs"
      );
    }
  }
);

export const fetchBookmarkedJobs = createAsyncThunk(
  "user/fetchBookmarkedJobs",
  async (_, thunkAPI) => {
    try {
      const response = await api.get("/api/user/bookmarked-jobs");
      return response.data.jobs || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch bookmarked jobs"
      );
    }
  }
);

export const fetchUserActivity = createAsyncThunk(
  "user/fetchActivity",
  async (_, thunkAPI) => {
    try {
      const response = await api.get("/api/user/activity");
      return response.data.activities || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch activity"
      );
    }
  }
);

export const fetchUserNotifications = createAsyncThunk(
  "user/fetchNotifications",
  async (_, thunkAPI) => {
    try {
      const response = await api.get("/api/user/notifications");
      return {
        notifications: response.data.notifications || [],
        unreadCount: response.data.unreadCount || 0,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch notifications"
      );
    }
  }
);

export const markUserNotificationRead = createAsyncThunk(
  "user/markNotificationRead",
  async (id, thunkAPI) => {
    try {
      const response = await api.patch(`/api/user/notifications/${id}/read`);
      return {
        notifications: response.data.notifications || [],
        unreadCount: response.data.unreadCount || 0,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to update notification"
      );
    }
  }
);

const initialState = {
  userInfo: localStorage.getItem("UserInfo")
    ? JSON.parse(localStorage.getItem("UserInfo"))
    : null,
  profile: null,
  dashboard: null,
  appliedJobs: [],
  bookmarkedJobs: [],
  activities: [],
  notifications: [],
  unreadNotifications: 0,
  status: "idle",
  error: null,
};

const syncUserInfoFromProfile = (state, profile) => {
  if (!state.userInfo) return;
  const existingDoc = state.userInfo.userDocument || {};
  const mergedDoc = {
    ...existingDoc,
    ...(profile || {}),
  };
  state.userInfo = {
    ...state.userInfo,
    userDocument: mergedDoc,
  };
  localStorage.setItem("UserInfo", JSON.stringify(state.userInfo));
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    logout: (state) => {
      state.userInfo = null;
      state.profile = null;
      state.dashboard = null;
      state.appliedJobs = [];
      state.bookmarkedJobs = [];
      state.activities = [];
      state.notifications = [];
      state.unreadNotifications = 0;
      localStorage.removeItem("UserInfo");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(loginUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload?.userType === "user") {
          state.userInfo = action.payload;
          localStorage.setItem("UserInfo", JSON.stringify(action.payload));
        } else {
          state.userInfo = null;
          localStorage.removeItem("UserInfo");
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || action.error.message;
      })
      .addCase(verifyEmailOtp.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(verifyEmailOtp.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(verifyEmailOtp.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(resendVerificationOtp.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(resendVerificationOtp.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(resendVerificationOtp.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchUserDashboard.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchUserDashboard.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.dashboard = action.payload;
      })
      .addCase(fetchUserDashboard.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchUserProfile.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.profile = action.payload;
        syncUserInfoFromProfile(state, action.payload);
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.profile = action.payload.user;
        syncUserInfoFromProfile(state, action.payload.user);
        if (state.dashboard) {
          state.dashboard.profile = {
            ...(state.dashboard.profile || {}),
            firstName: action.payload.user?.firstName || state.dashboard.profile?.firstName || "",
            lastName: action.payload.user?.lastName || state.dashboard.profile?.lastName || "",
            email: action.payload.user?.email || state.dashboard.profile?.email || "",
            avatar: action.payload.user?.avatar || "",
          };
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(uploadUserAvatar.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(uploadUserAvatar.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.profile = action.payload.user;
        syncUserInfoFromProfile(state, action.payload.user);
        if (state.dashboard) {
          state.dashboard.profile = {
            ...(state.dashboard.profile || {}),
            avatar: action.payload.user?.avatar || "",
          };
        }
      })
      .addCase(uploadUserAvatar.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(updateUserSettings.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.profile = action.payload.user;
        syncUserInfoFromProfile(state, action.payload.user);
      })
      .addCase(updateUserSettings.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(changeUserPassword.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(changeUserPassword.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchAppliedJobs.fulfilled, (state, action) => {
        state.appliedJobs = action.payload;
      })
      .addCase(fetchAppliedJobs.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchBookmarkedJobs.fulfilled, (state, action) => {
        state.bookmarkedJobs = action.payload;
      })
      .addCase(fetchBookmarkedJobs.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchUserActivity.fulfilled, (state, action) => {
        state.activities = action.payload;
      })
      .addCase(fetchUserActivity.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchUserNotifications.fulfilled, (state, action) => {
        state.notifications = action.payload.notifications;
        state.unreadNotifications = action.payload.unreadCount;
      })
      .addCase(fetchUserNotifications.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(markUserNotificationRead.fulfilled, (state, action) => {
        state.notifications = action.payload.notifications;
        state.unreadNotifications = action.payload.unreadCount;
      })
      .addCase(markUserNotificationRead.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      });
  },
});

export const { logout } = userSlice.actions;
export default userSlice.reducer;
